import { fg } from '@atlassian/jira-feature-gating';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import type { JiraWorkManagementNavData } from '@atlassian/jira-navigation-apps-resources/src/services/sidebar/project-sidebar-navigation/types.tsx';
import type { Features, ReportFeatures, SettingFeatures } from '../../../common/types/index.tsx';

export const getExtraData = (data: JiraWorkManagementNavData, isPremium: boolean): Features => {
	const { permissionInfo, hasComponents = false, project } = data;
	const { isAdmin, projectPermissionsQueried } = permissionInfo;
	const isProjectOrJiraAdmin = projectPermissionsQueried.ADMINISTER_PROJECTS === true || isAdmin;
	const isSimplifiedProject = project.simplified;

	const hasSettings =
		isAdmin ||
		projectPermissionsQueried.ADMINISTER_PROJECTS ||
		projectPermissionsQueried.EDIT_ISSUE_LAYOUT ||
		projectPermissionsQueried.EDIT_WORKFLOW ||
		(fg('vmc-11-manage-versions-permission') ? projectPermissionsQueried.MANAGE_VERSIONS : false);

	return {
		hasVersions: fg('vmc-11-manage-versions-permission')
			? projectPermissionsQueried.MANAGE_VERSIONS || isProjectOrJiraAdmin
			: true,
		hasSettings,
		hasBoard: true,
		hasReports: true,
		hasAddItem: isProjectOrJiraAdmin,
		hasComponents,
		hasIssues: true,
		hasSummary: true,
		hasPages: true,
		hasChangeTheme: isProjectOrJiraAdmin,
		hasApprovals: isSimplifiedProject && (isPremium || isProjectOrJiraAdmin),
	};
};

export const getExtraSettingsData = (
	data: JiraWorkManagementNavData,
	hasActiveSoftwareAccess: boolean,
): SettingFeatures => {
	const { isAdmin, projectPermissionsQueried } = data.permissionInfo;
	const isProjectAdmin = projectPermissionsQueried.ADMINISTER_PROJECTS === true;
	const isProjectOrJiraAdmin = isProjectAdmin || isAdmin;
	const isCMPProject = !data.project.simplified;

	const hasEditWorkflowPermissions = projectPermissionsQueried.EDIT_WORKFLOW === true;
	const hasIssueLayoutPermissions = projectPermissionsQueried.EDIT_ISSUE_LAYOUT === true;
	const hasManageVersionsPermissions = projectPermissionsQueried.MANAGE_VERSIONS === true;

	const hasDetails = isProjectOrJiraAdmin;
	const hasPeople = isProjectOrJiraAdmin;
	const hasIssueTypes = isAdmin;

	let hasAutomation = true;
	let hasSummary = true;
	let hasWorkflows = true;
	let hasScreens = true;
	/*
	 * SettingFeatures.hasSystemFieldsConfiguration and hasProjectEfaFields both
	 * show/hide a navigation menu item named "Fields".
	 *
	 * - `hasSystemFieldsConfiguration` is CMP specific; shows the
	 * "System Default Field Configuration" page which is visible by default
	 * - `hasProjectEfaFields` is TMP specific; only shows Project Fields page
	 * under certain circumstances.
	 */
	let hasSystemFieldsConfiguration = true;
	let hasProjectEfaFields = false;
	let hasVersions = true;
	let hasComponents = true;
	let hasPermissions = true;
	let hasIssueSecurity = true;
	let hasNotifications = true;
	let hasIssueCollectors = true;
	let hasIssueLayout = true;

	const hasDevtools = isProjectAdmin && hasActiveSoftwareAccess;

	hasAutomation = isProjectOrJiraAdmin;
	hasSummary = isProjectOrJiraAdmin;
	hasWorkflows = hasEditWorkflowPermissions || isProjectOrJiraAdmin;
	hasScreens = isProjectOrJiraAdmin;
	hasSystemFieldsConfiguration = isCMPProject && isProjectOrJiraAdmin;
	hasVersions = fg('vmc-11-manage-versions-permission')
		? hasManageVersionsPermissions || isProjectOrJiraAdmin
		: isProjectOrJiraAdmin;
	hasComponents = isProjectOrJiraAdmin;
	hasPermissions = isProjectOrJiraAdmin;
	hasIssueSecurity = isProjectOrJiraAdmin;
	hasNotifications = isProjectOrJiraAdmin;
	hasIssueCollectors = isProjectOrJiraAdmin;
	hasIssueLayout = hasIssueLayoutPermissions || isProjectOrJiraAdmin;

	const [expConfig] = UNSAFE_noExposureExp('efa_and_project_fields_page_for_tmp');
	if (expConfig.get('isEnabled', false)) {
		hasProjectEfaFields =
			!isCMPProject && isProjectOrJiraAdmin && !!data.hasExplicitFieldAssociationsEnabled;
	}

	return {
		hasAutomation,
		hasProjectGroupOneSection: hasDetails || hasPeople || hasSummary,
		hasIssueConfigSection: hasIssueTypes,
		hasProjectGroupTwoSection: hasWorkflows || hasScreens || hasSystemFieldsConfiguration,
		hasProjectGroupThreeSection: hasVersions || hasComponents,
		hasProjectGroupFourSection: hasPermissions || hasIssueSecurity || hasNotifications,
		hasgetIssueCollectorSection: hasIssueCollectors,
		hasComponents,
		hasDetails,
		hasSystemFieldsConfiguration,
		hasProjectEfaFields,
		hasIssueCollectors,
		hasIssueSecurity,
		hasIssueTypes,
		hasNotifications,
		hasPeople,
		hasPermissions,
		hasScreens,
		hasSummary,
		hasVersions,
		hasWorkflows,
		hasDevtools,
		hasIssueLayout,
	};
};

export const getExtraReportData = (hasSoftwareAccess: boolean): ReportFeatures => ({
	hasDeveloperWorkload: true,
	hasForcastSection: true,
	hasTimeTracking: true,
	hasVersionWorkloadReport: hasSoftwareAccess,
});
